import { createDateColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/DateColumn';
import { getExpectedTransactionTypeText } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/utils/expectedTransactionType.utils.ts';
import { MetaBadge } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/components/MetaBadge/MetaBadge.tsx';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { DATE_FORMATS } from '@dealstack/shared/lib/format/dates/date';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

import { CollapsedListCount } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/components/CollapsedListCount.tsx';
import { ExpandedListContainer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/components/ExpandedListContainer.tsx';
import { FooterStatAllocations } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/components/FooterStatAllocations.tsx';
import { FooterStatByStatus } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/components/FooterStatByStatus.tsx';
import { FooterUniqueStat } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/components/FooterUniqueStat.tsx';
import { InvestmentRowActions } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/components/InvestmentRowActions/InvestmentRowActions.tsx';
import { StatusTooltipCount } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/components/StatusTooltipCount.tsx';
import { TextStatusRow } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/components/TextStatusRow.tsx';
import {
	getDeclarationsSummary,
	getGroupedContractStatus,
	getGroupedKycDisplayStatus,
	getInvestmentSecuritySummary,
	getInvestmentType,
	getKycStatusFilterOptions,
	getNormalizedKycReviewStatus,
	getOptionsFromEnum,
	getTranslatedInvestmentType,
	getTranslatedKycFileType,
	getTranslationForPaymentStatus,
	getTranslationFromStatus,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/utils/investment.helper.ts';
import {
	ManageInvestmentsTableColumn,
	ORDERED_CONTRACTS_STATUSES,
	ORDERED_KYC_STATUSES,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/utils/investment.types.ts';
import { EXPECTED_STATUS } from '@dealstack/shared/lib/common/business/kyc';
import {
	getColorFromStatus,
	getIconFromStatus,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/utils/statusStyling.helper.ts';
import { createSortableTextHeader } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnHeader/ColumnHeader.tsx';
import { CapTableSelect } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineCapSelect/CapTableSelect.tsx';
import {
	ExtendedColumn,
	ExtendedColumnDef,
} from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { InvestorColumnSummary } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/components/InvestorColumnSummary';
import { StatusBadge } from '@dealstack/frontend/shared/components/StatusBadge/StatusBadge.tsx';
import { getInvestmentStatusBadge } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/components/investmentStatusBadge.ts';
import { formatNumber } from '@dealstack/shared/lib/format/number';
import {
	AcceptanceStatus,
	DocumentStatus,
	GetInvestmentsForMipItem,
	GetInvestmentsInvestorInfo,
	InvestmentStats,
	InvestmentWorkflowStatus,
	InvestmentWorkflowType,
	KYCFileType,
	TransactionState,
} from '@dealstack/shared/lib/generated';
import { Flex, Text } from '@mantine/core';
import { MIP_MANAGE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes';
import {
	documentsForInvestmentPath,
	investorDetailsPath,
	mipManagePath,
} from '@dealstack/frontend/app/pages/Routing/routes.utils';
import { createCohortsColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/CohortsColumn.tsx';

interface Props {
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>;
	tCommonUi: TranslateFunction<I18Namespaces.COMMON_UI>;
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>;
	dealId: string;
	mipId: string;
	securitiesOptions?: { label: string; value: string }[];
	mipClassifierFilterOptions?: { label: string; value: string }[];
	onKycClick?: (kycWorkflowId: string) => void;
	onInvestorDataClick?: (kycWorkflowId: string) => void;
	onTaxComplianceClick?: (kycWorkflowId: string) => void;
	onInviteClick?: (investor: GetInvestmentsInvestorInfo) => void;
	onRevokeClick?: (investmentWorkflowId: string) => void;
	onReleaseClick?: (investorId: string) => void;
	onVestingTermsClick?: (investorId: string) => void;
	hasEditorPermission?: boolean;
	hasKycAdminPermission?: boolean;
	stats?: InvestmentStats;
	disableSendInvestorInvitation?: boolean;
	currentPath?: string;
}

const EmptyColumnText = ({ text }: { text: string }) => (
	<Text fw={400} c="dark.0" fz="sm" fs="italic">
		{text}
	</Text>
);

export const getInvestmentsTableColumns = ({
	tMipManage,
	tCommonUi,
	tCapManage,
	dealId,
	mipId,
	securitiesOptions = [],
	mipClassifierFilterOptions = [],
	onKycClick,
	onInvestorDataClick,
	onTaxComplianceClick,
	onInviteClick,
	onRevokeClick,
	onReleaseClick,
	onVestingTermsClick,
	hasEditorPermission = false,
	hasKycAdminPermission = false,
	stats,
	disableSendInvestorInvitation = false,
	currentPath = '',
}: Props): ExtendedColumnDef<GetInvestmentsForMipItem>[] => {
	const investmentWorkflowTypeOptions = getOptionsFromEnum(
		InvestmentWorkflowType,
		(value: InvestmentWorkflowType) => getTranslatedInvestmentType(value, tMipManage)
	);
	const contractStatusOptions = getOptionsFromEnum(DocumentStatus, (value: DocumentStatus) =>
		getTranslationFromStatus(value, tMipManage)
	);
	const paymentStatusOptions = getOptionsFromEnum(TransactionState, (value: TransactionState) =>
		getTranslationForPaymentStatus(value, tMipManage)
	);
	const investmentStatusOptions = getOptionsFromEnum(
		InvestmentWorkflowStatus,
		(value: InvestmentWorkflowStatus) =>
			getInvestmentStatusBadge(value, tMipManage, 'light').text
	);

	return [
		{
			cell: ({ row }) => (
				<Flex h="100%" w="100%" align="flex-start" justify="center" px="12px">
					<Flex
						h="42px"
						align="center"
						justify="center"
						style={{ cursor: 'pointer' }}
						onClick={(e) => {
							e.stopPropagation();
							row.toggleExpanded();
						}}
					>
						<Icon
							variant="caret-right-new"
							size="xl"
							style={{
								transform: row.getIsExpanded() ? 'rotate(90deg)' : 'rotate(0deg)',
								transition: 'transform 0.2s ease-in-out',
							}}
						/>
					</Flex>
				</Flex>
			),
			header: '',
			id: 'actions',
			minSize: 36,
			size: 36,
			meta: { isGrow: false },
		},
		createTextColumn({
			accessorKey: ManageInvestmentsTableColumn.investmentWorkflowId,
			headerText: tMipManage('investments.table.columns.id'),
			columnLabel: tMipManage('investments.table.columns.id'),
			enableSorting: false,
			filterValueType: 'uuid',
		}),
		// Hidden by default: deep links (the investor details quick links) filter by it, since
		// the investor column is a free-text search.
		createTextColumn({
			id: ManageInvestmentsTableColumn.investorId,
			accessorFn: (row) => row.investor.id,
			headerText: tMipManage('investments.table.columns.investor_id'),
			columnLabel: tMipManage('investments.table.columns.investor_id'),
			filterText: tMipManage('investments.table.columns.investor_id'),
			enableSorting: false,
			showFilter: true,
			filterValueType: 'uuid',
		}),
		createTextColumn({
			accessorKey: ManageInvestmentsTableColumn.investor,
			headerText: tMipManage('investments.table.columns.investor'),
			columnLabel: tMipManage('investments.table.columns.investor'),
			filterText: tMipManage('investments.table.filter.investor'),
			showFilter: true,
			enableSorting: true,
			size: 240,
			minSize: 200,
			accessorFn: (row) => row.investor,
			cell: ({ row }) => {
				const value = row.original.investor;
				const isExpanded = row.getIsExpanded();
				if (!value) {
					return (
						<EmptyColumnText
							text={tMipManage('investments.table.columns.investor_empty')}
						/>
					);
				}
				const investorUrl = investorDetailsPath({
					dealId,
					mipId,
					investorId: value.id ?? '',
				});

				return (
					<ExpandedListContainer
						link={isExpanded ? investorUrl : null}
						linkLabel={tMipManage('investments.table.links.review_investor')}
					>
						<InvestorColumnSummary
							displayName={value.displayName}
							email={value.email}
						/>
					</ExpandedListContainer>
				);
			},
			showFooter: true,
			footer: () => (
				<FooterUniqueStat label="Unique count" value={stats?.investorCount ?? 0} />
			),
		}),
		createCohortsColumn({
			id: ManageInvestmentsTableColumn.cohorts,
			headerText: tMipManage('investments.table.columns.cohorts'),
			columnLabel: tMipManage('investments.table.columns.cohorts'),
			filterText: tMipManage('investments.table.filter.cohorts'),
			enableSorting: true,
			filterOptions: mipClassifierFilterOptions,
			getCohorts: (row) => row.investor.mipClassifiers ?? [],
			size: 200,
			minSize: 160,
		}),
		createTextColumn({
			accessorKey: ManageInvestmentsTableColumn.type,
			headerText: tMipManage('investments.table.columns.type'),
			filterText: tMipManage('investments.table.filter.type'),
			columnLabel: tMipManage('investments.table.columns.type'),
			accessorFn: (row) => getInvestmentType(row.type, tMipManage),
			enableSorting: false,
			showFilter: true,
			filterVariant: 'select',
			mantineFilterSelectProps: {
				data: investmentWorkflowTypeOptions,
			},
			size: 140,
			minSize: 120,
		}),
		createTextColumn({
			accessorKey: ManageInvestmentsTableColumn.allocations,
			headerText: tMipManage('investments.table.columns.allocations'),
			columnLabel: tMipManage('investments.table.columns.allocations'),
			enableSorting: false,
			size: 380,
			minSize: 300,
			showFilter: true,
			filterText: tMipManage('investments.table.filter.securities'),
			filterVariant: 'multi-select',
			mantineFilterMultiSelectProps: {
				data: securitiesOptions,
			},
			accessorFn: (row) =>
				row.allocations?.map((allocation) => ({
					...allocation,
					name: getInvestmentSecuritySummary(tCommonUi, allocation),
				})),
			cell: ({ row, getValue }) => {
				const isExpanded = row.getIsExpanded();
				const allocations = getValue();

				if (!Array.isArray(allocations) || !allocations.length) {
					return (
						<EmptyColumnText
							text={tMipManage('investments.table.columns.allocations_empty')}
						/>
					);
				}

				if (isExpanded) {
					return (
						<ExpandedListContainer>
							{allocations.map((allocation) => (
								<Flex key={allocation.name} align="center" gap="0.25rem">
									{allocation.expectedTransactionType && (
										<MetaBadge
											label={getExpectedTransactionTypeText(
												allocation.expectedTransactionType,
												tCapManage
											)}
										/>
									)}
									<Text truncate fw={400}>
										{allocation.name}
									</Text>
								</Flex>
							))}
						</ExpandedListContainer>
					);
				}

				return (
					<CollapsedListCount count={allocations.length - 1}>
						{allocations[0].expectedTransactionType && (
							<MetaBadge
								label={getExpectedTransactionTypeText(
									allocations[0].expectedTransactionType,
									tCapManage
								)}
							/>
						)}
						<Text fw={400} truncate lineClamp={1} style={{ flex: 1 }}>
							{allocations[0].name}
						</Text>
					</CollapsedListCount>
				);
			},
			showFooter: true,
			footer: () => {
				const allocations = Object.values(stats?.allocationsCount || {}).map((alloc) =>
					getInvestmentSecuritySummary(tCommonUi, alloc)
				);
				return <FooterStatAllocations countSummaryItems={allocations} />;
			},
		}),
		createTextColumn({
			accessorKey: ManageInvestmentsTableColumn.kyc,
			headerText: tMipManage('investments.table.columns.kyc'),
			columnLabel: tMipManage('investments.table.columns.kyc'),
			filterText: tMipManage('investments.table.filter.kyc'),
			accessorFn: (row) => row.kyc,
			enableSorting: false,
			size: 200,
			minSize: 160,
			showFilter: true,
			filterVariant: 'select',
			mantineFilterSelectProps: {
				data: getKycStatusFilterOptions(tMipManage),
			},
			cell: ({ row }) => {
				const isExpanded = row.getIsExpanded();
				const kyc = Array.isArray(row.original.kyc) ? row.original.kyc : [];
				const expectedKyc = Array.isArray(row.original.expectedKyc)
					? row.original.expectedKyc
					: [];
				const expectedForms = Array.isArray(row.original.expectedForms)
					? row.original.expectedForms
					: [];
				const declarations = Array.isArray(row.original.declarations)
					? row.original.declarations
					: [];
				const isKycRequired = row.original.isKycRequired ?? true;
				const anyDeclarationRequired =
					(row.original.isBeneficialOwnershipRequired ?? true) ||
					(row.original.isSourceOfFundsRequired ?? true) ||
					(row.original.isPepRequired ?? true);

				if (!isKycRequired) {
					return (
						<Text fw={400} c="dark.0" fz="sm" fs="italic">
							{tMipManage('investments.table.columns.kyc_not_required')}
						</Text>
					);
				}

				if (isExpanded) {
					const kycWorkflowParam = row.original.kycWorkflowId
						? `&kycWorkflowId=${row.original.kycWorkflowId}`
						: '';
					const kycUrl = row.original.investor
						? `${mipManagePath(`${MIP_MANAGE_ROUTES.KYC_PAGE}/main/:investorId`, {
								dealId,
								mipId,
								investorId: row.original.investor.id,
							})}?kycEntryPointUrl=${encodeURIComponent(currentPath)}${kycWorkflowParam}`
						: null;

					const declarationsSummary =
						declarations.length > 0
							? getDeclarationsSummary(declarations, tMipManage)
							: null;

					const kycWithoutForms = kyc.filter((item) => item.type !== KYCFileType.FORM);
					const expectedKycWithoutForms = expectedKyc.filter(
						(item) => item.type !== KYCFileType.FORM
					);

					return (
						<ExpandedListContainer
							link={hasKycAdminPermission ? kycUrl : null}
							linkLabel={tMipManage('investments.table.links.review_kyc')}
						>
							{anyDeclarationRequired &&
								(declarationsSummary ? (
									<TextStatusRow
										name={declarationsSummary.text}
										status={
											declarationsSummary.hasInherited
												? EXPECTED_STATUS
												: declarationsSummary.overallStatus
										}
										isInherited={declarationsSummary.hasInherited}
										isKyc
									/>
								) : (
									<TextStatusRow
										name={tMipManage('kyc_type.declarations')}
										status={EXPECTED_STATUS}
										isKyc
									/>
								))}
							{kycWithoutForms.map((item) => (
								<TextStatusRow
									key={item.id}
									name={getTranslatedKycFileType(item.type, tMipManage)}
									status={
										item.isInherited
											? EXPECTED_STATUS
											: getNormalizedKycReviewStatus(item.status)
									}
									isInherited={item.isInherited}
									isKyc
								/>
							))}
							{expectedKycWithoutForms
								.filter((item) => !item.isSubmitted)
								.map((item) => (
									<TextStatusRow
										key={`expected-${item.type}`}
										name={getTranslatedKycFileType(item.type, tMipManage)}
										status={EXPECTED_STATUS}
										isKyc
									/>
								))}
							{expectedForms.map((form) => {
								const isInherited = form.isSubmitted && form.isInherited;
								let status: AcceptanceStatus | typeof EXPECTED_STATUS;
								if (!form.isSubmitted || isInherited) {
									status = EXPECTED_STATUS;
								} else {
									status = form.status ?? AcceptanceStatus.PENDING;
								}
								return (
									<TextStatusRow
										key={`form-${form.templateId}`}
										name={form.formName}
										status={status}
										isInherited={isInherited}
										isKyc
									/>
								);
							})}
						</ExpandedListContainer>
					);
				}

				return (
					<Flex gap="0.5rem" wrap="wrap" key="collapsed">
						{getGroupedKycDisplayStatus(
							kyc,
							expectedKyc,
							expectedForms,
							declarations,
							tMipManage,
							anyDeclarationRequired
						).map((group) => (
							<StatusTooltipCount
								key={group.status}
								items={group.items}
								status={group.status}
								isKyc
							/>
						))}
					</Flex>
				);
			},
			showFooter: true,
			footer: () => (
				<FooterStatByStatus
					orderedStatuses={ORDERED_KYC_STATUSES}
					countsByStatus={stats?.kycStatusCounts || {}}
					isKyc
				/>
			),
		}),
		createTextColumn({
			accessorKey: ManageInvestmentsTableColumn.contracts,
			header: () => <Text fw={600}>{tMipManage('investments.table.columns.contracts')}</Text>,
			columnLabel: tMipManage('investments.table.columns.contracts'),
			accessorFn: (row) => row.contracts,
			showFilter: true,
			filterVariant: 'select',
			filterText: tMipManage('investments.table.filter.contracts'),
			mantineFilterSelectProps: {
				data: contractStatusOptions,
			},
			minSize: 200,
			size: 200,
			cell: ({ row }) => {
				const isExpanded = row.getIsExpanded();
				let contracts = row.original.contracts;
				contracts = Array.isArray(contracts) ? contracts : [];

				if (isExpanded) {
					if (!contracts.length) {
						return (
							<EmptyColumnText
								text={tMipManage('investments.table.columns.contracts_empty')}
							/>
						);
					}

					const contractsUrl = documentsForInvestmentPath({
						dealId,
						mipId,
						investmentWorkflowId: row.original.investmentWorkflowId,
					});

					return (
						<ExpandedListContainer
							link={contractsUrl}
							linkLabel={tMipManage('investments.table.links.review_contracts')}
						>
							{contracts.map((item) => (
								<TextStatusRow
									key={item.id}
									name={item.name}
									status={item.status}
								/>
							))}
						</ExpandedListContainer>
					);
				}

				return (
					<Flex gap="0.5rem" wrap="wrap" key="not-expanded">
						{getGroupedContractStatus(contracts).map((group) => (
							<StatusTooltipCount
								key={group.status}
								status={group.status}
								items={group.items}
							/>
						))}
					</Flex>
				);
			},
			showFooter: true,
			footer: () => (
				<FooterStatByStatus
					orderedStatuses={ORDERED_CONTRACTS_STATUSES}
					countsByStatus={stats?.contractStatusCounts || {}}
				/>
			),
		}),
		{
			accessorKey: ManageInvestmentsTableColumn.payment,
			header: createSortableTextHeader(
				tMipManage('investments.table.columns.fundsTransfer'),
				true,
				{
					style: {
						marginRight: 0,
						marginLeft: 'auto',
					},
				},
				'left'
			),
			columnLabel: tMipManage('investments.table.columns.fundsTransfer'),
			cell: ({ row }) => {
				const isExpanded = row.getIsExpanded();
				const currency = row.original.considerationCurrency;
				const payment = row.original.payment;
				const statusIcon = getIconFromStatus(payment.status);
				const value = formatNumber({
					value: payment.cost,
					currency,
					lockPrecision: true,
					minPrecision: 2,
					maxPrecision: 2,
					returnNumericalZero: true,
				});

				return (
					<Flex justify="flex-end" w="100%">
						{isExpanded ? (
							<TextStatusRow
								status={payment.status}
								name={value}
								direction="row-reverse"
							/>
						) : (
							<Flex
								gap="0.25rem"
								align="center"
								c={getColorFromStatus(payment.status)}
							>
								{!!statusIcon && <Icon variant={statusIcon} />}
								<Text c="dark.9">{value}</Text>
							</Flex>
						)}
					</Flex>
				);
			},
			enableSorting: false,
			showFilter: true,
			filterVariant: 'select',
			filter: ({ column }) => (
				<CapTableSelect
					column={column as ExtendedColumn<unknown>}
					filterText={tMipManage('investments.table.filter.payment')}
				/>
			),
			mantineFilterSelectProps: {
				data: paymentStatusOptions,
			},
			size: 140,
			minSize: 120,
			footer: () => {
				const value = formatNumber({
					value: stats?.fundsTransferTotal ?? 0,
					currency: stats?.fundsTransferCurrency ?? undefined,
					lockPrecision: true,
					minPrecision: 2,
					maxPrecision: 2,
					returnNumericalZero: true,
				});

				return <FooterUniqueStat value={value} align="flex-end" />;
			},
		},
		createTextColumn({
			accessorKey: ManageInvestmentsTableColumn.status,
			headerText: tMipManage('investments.table.columns.status'),
			columnLabel: tMipManage('investments.table.columns.status'),
			filterText: tMipManage('investments.table.filter.status'),
			showFilter: true,
			enableSorting: true,
			filterVariant: 'select',
			mantineFilterSelectProps: {
				data: investmentStatusOptions,
			},
			accessorFn: (row) => row.status,
			cell: ({ row }) => {
				const badge = getInvestmentStatusBadge(row.original.status, tMipManage, 'light');
				return (
					<StatusBadge
						text={badge.text}
						color={badge.color}
						textColor={badge.textColor}
					/>
				);
			},
			size: 140,
			minSize: 120,
		}),
		createDateColumn({
			dateFormat: DATE_FORMATS.CALENDAR_DATE,
			headerText: tMipManage('investments.table.columns.createdAt'),
			columnLabel: tMipManage('investments.table.columns.createdAt'),
			filterText: tMipManage('investments.table.filter.createdAt'),
			showFilter: true,
			accessorKey: ManageInvestmentsTableColumn.createdAt,
			accessorFn: (row) => {
				if (!row.createdAt) {
					return null;
				}
				return new Date(row.createdAt);
			},
			filterVariant: 'date-range',
			filterValueType: 'date-range',
			size: 160,
			minSize: 130,
			showFooter: true,
			footer: () => (
				<FooterUniqueStat label="Unique count" value={stats?.createdAtUniqueCount ?? 0} />
			),
		}),
		createDateColumn({
			dateFormat: DATE_FORMATS.CALENDAR_DATE,
			headerText: tMipManage('investments.table.columns.updatedAt'),
			columnLabel: tMipManage('investments.table.columns.updatedAt'),
			filterText: tMipManage('investments.table.filter.updatedAt'),
			showFilter: true,
			accessorKey: ManageInvestmentsTableColumn.updatedAt,
			accessorFn: (row) => {
				if (!row.updatedAt) {
					return null;
				}
				return new Date(row.updatedAt);
			},
			filterVariant: 'date-range',
			filterValueType: 'date-range',
			size: 160,
			minSize: 130,
		}),
		...(onKycClick && onInvestorDataClick && hasEditorPermission
			? [
					{
						cell: ({ row }) => (
							<InvestmentRowActions
								onKycClick={() => onKycClick(row.original.kycWorkflowId)}
								onInvestorDataClick={() =>
									onInvestorDataClick(row.original.kycWorkflowId)
								}
								onTaxComplianceClick={
									onTaxComplianceClick
										? () => onTaxComplianceClick(row.original.kycWorkflowId)
										: undefined
								}
								onInviteClick={() => onInviteClick?.(row.original.investor)}
								onRevokeClick={() =>
									onRevokeClick?.(row.original.investmentWorkflowId)
								}
								onReleaseClick={() => onReleaseClick?.(row.original.investor.id)}
								onVestingTermsClick={() =>
									onVestingTermsClick?.(row.original.investor.id)
								}
								isCompleted={row.original.isCompleted}
								disableInvite={disableSendInvestorInvitation}
								disableVestingTerms={!hasEditorPermission}
								isEligibleForRelease={row.original.isEligibleForRelease ?? false}
							/>
						),
						header: '',
						id: 'row-actions',
						enableGlobalFilter: false,
						enableColumnFilter: false,
						enableHiding: false,
						showFilter: false,
						minSize: 56,
						size: 56,
						meta: { isGrow: false },
					} as ExtendedColumnDef<GetInvestmentsForMipItem>,
				]
			: []),
	];
};
